import {
  Broadcast,
  ChatCircleText,
  ListBullets,
  QrCode,
  SquareHalf,
  SquaresFour,
} from "@phosphor-icons/react";
import type {
  AudienceArtifact,
  ProjectorBoardSort,
  ProjectorState,
  RoomThread,
} from "../../shared/types.ts";
import {
  compareMostReplies,
  compareMostUpvotes,
  compareOldestThreads,
  DEFAULT_PROJECTOR_BOARD_SORT,
} from "../../shared/threadSorting.ts";
import { authorDisplay, sortableThread, splitSections } from "../lib/threads.ts";

export interface ProjectorStripActions {
  showBoard: (boardSort: ProjectorBoardSort) => void;
  showJoinQr: () => void;
  showKeyThemes: () => void;
  showKeyThemesBoard: () => void;
  setRepliesVisible: (visible: boolean) => void;
}

/** Dark-slate "mirrors the room screen" status block — the one place the dark
 *  treatment is allowed. Shared by the admin rail card and the moderator
 *  projector card; the participant hero kicker uses the same visual language. */
export function OnScreenStatus({
  projector,
  threads,
  artifacts,
}: {
  projector: ProjectorState | null;
  threads: RoomThread[];
  artifacts: AudienceArtifact[];
}) {
  const { mode, sub } = describeOnScreen(projector, threads, artifacts);
  return (
    <>
      <div className="kick"><Broadcast weight="fill" /> On screen now</div>
      <div className="mode">{mode}</div>
      <div className="os-sub" data-testid="projector-strip-body">{sub}</div>
    </>
  );
}

/** Row-style projection controls (QR / Board+sort / Question+replies) — the
 *  admin design, now shared with the moderator. */
export function ProjectorControls({
  projector,
  current,
  pending = false,
  actions,
}: {
  projector: ProjectorState | null;
  current: RoomThread | null;
  pending?: boolean;
  actions: ProjectorStripActions;
}) {
  const boardSort: ProjectorBoardSort = projector?.boardSort ?? DEFAULT_PROJECTOR_BOARD_SORT;
  const repliesVisible = projector?.showReplies ?? true;
  const mode = projector?.mode ?? null;
  const segments = [
    { key: "join-qr", caption: "QR", title: "Join QR", icon: <QrCode weight="bold" />, onClick: () => actions.showJoinQr(), disabled: pending },
    { key: "key-themes", caption: "Live", title: "Discussion + Key Themes", icon: <SquareHalf weight="bold" />, onClick: () => actions.showKeyThemes(), disabled: pending },
    { key: "key-themes-board", caption: "Themes", title: "Key Themes board", icon: <SquaresFour weight="bold" />, onClick: () => actions.showKeyThemesBoard(), disabled: pending },
    { key: "board", caption: "Board", title: "Question board", icon: <ListBullets weight="bold" />, onClick: () => actions.showBoard(boardSort), disabled: pending },
    { key: "thread", caption: "Question", title: "Projected question (project one from the queue)", icon: <ChatCircleText weight="bold" />, onClick: undefined, disabled: !current || pending },
  ] as const;
  return (
    <div className="projctl projctl-seg" data-testid="projector-controls">
      <div className="projctl-seg-row" role="group" aria-label="Projector mode">
        {segments.map((segment) => (
          <button
            key={segment.key}
            type="button"
            className={`projctl-seg-btn${mode === segment.key ? " is-active" : ""}`}
            title={segment.title}
            aria-label={segment.title}
            aria-pressed={mode === segment.key}
            disabled={segment.disabled || !segment.onClick}
            onClick={segment.onClick}
          >
            {mode === segment.key && <span className="projctl-seg-dot" aria-hidden="true" />}
            {segment.icon}
            <span className="projctl-seg-caption">{segment.caption}</span>
          </button>
        ))}
      </div>
      {mode === "board" && (
        <div className="projctl-seg-options">
          <span>Sort</span>
          <select
            className="projctl-select"
            aria-label="Board sort"
            value={boardSort}
            disabled={pending}
            onChange={(e) => actions.showBoard(e.target.value as ProjectorBoardSort)}
          >
            <option value="oldest_unanswered">Oldest</option>
            <option value="most_liked">Upvotes</option>
            <option value="most_replied">Replies</option>
          </select>
        </div>
      )}
      {mode === "thread" && (
        <div className="projctl-seg-options">
          <button
            type="button"
            className="projctl-option-btn"
            disabled={!current || pending}
            onClick={() => actions.setRepliesVisible(!repliesVisible)}
          >
            {repliesVisible ? "Replies on" : "Replies off"}
          </button>
        </div>
      )}
    </div>
  );
}

export function currentProjectedThread(
  projector: ProjectorState | null,
  threads: RoomThread[],
): RoomThread | null {
  return projector?.mode === "thread"
    ? threads.find((t) => t.id === projector.projectedThreadId) ?? null
    : null;
}

function describeOnScreen(
  projector: ProjectorState | null,
  threads: RoomThread[],
  artifacts: AudienceArtifact[],
): { mode: string; sub: string } {
  if (!projector) {
    return { mode: "Nothing on screen", sub: "The projector has not shown anything yet." };
  }

  if (projector.mode === "thread") {
    const current = threads.find((t) => t.id === projector.projectedThreadId) ?? null;
    if (!current) return { mode: "Question", sub: "The projected question is no longer available." };
    const author = authorDisplay(current);
    return {
      mode: "Question",
      sub: `“${truncate(current.body)}”${author.label ? ` — ${author.label}` : ""}`,
    };
  }

  if (projector.mode === "join-qr") {
    return { mode: "Join QR", sub: "The room screen is showing the XL join code." };
  }

  if (projector.mode === "key-themes") {
    return { mode: "Discussion + key themes", sub: "Live discussion and consolidated key themes." };
  }

  if (projector.mode === "key-themes-board") {
    return { mode: "Key themes board", sub: "The consolidated key themes, updated live." };
  }

  if (projector.mode === "artifact") {
    const artifact = artifacts.find((a) => a.id === projector.projectedArtifactId) ?? null;
    return { mode: "Summary", sub: artifact ? truncate(artifact.body) : "A summary is projected." };
  }

  // board
  const { live } = splitSections(threads);
  const top = topBoardThread(live, projector.boardSort);
  return {
    mode: `Board · ${live.length} question${live.length === 1 ? "" : "s"}`,
    sub: top
      ? `${boardSortLabel(projector.boardSort)}. Top: “${truncate(top.body)}”`
      : "Live board — no questions shown yet.",
  };
}

function topBoardThread(board: RoomThread[], boardSort: ProjectorState["boardSort"]): RoomThread | null {
  if (board.length === 0) return null;
  const sorted = [...board].sort((a, b) => {
    if (boardSort === "most_liked") return compareMostUpvotes(sortableThread(a), sortableThread(b));
    if (boardSort === "most_replied") return compareMostReplies(sortableThread(a), sortableThread(b));
    return compareOldestThreads(sortableThread(a), sortableThread(b));
  });
  return sorted[0];
}

function boardSortLabel(boardSort: ProjectorState["boardSort"]): string {
  if (boardSort === "most_liked") return "Sorted by upvotes";
  if (boardSort === "most_replied") return "Sorted by most replies";
  return "Sorted by oldest unanswered";
}

function truncate(text: string, max = 90): string {
  const trimmed = text.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}
